import React, { Fragment, useContext, useEffect, useState } from "react";
import { Row, Col, Button, Card, Form, Alert, Collapse, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Prompt } from "react-router-dom";
import { abpnClient, IdentityServerKnownClient } from "../../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import ActivateUser from "../Auth/Models/ActivateUser";
import Physician from "../Physician/Models/Physician";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import PasswordControl from "../Shared/Components/PasswordControl";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import ChangeEmailModel from "./Models/ChangeEmailModel";
import ChangePasswordModel from "./Models/ChangePasswordModel";

interface IProps {
    isSectionOpen: boolean;
    physician: Physician;
}

export default function SecurityCard(props: IProps) {
    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentEmail, setCurrentEmail] = useState<string>(props.physician.emailAddress);
    const [updatedEmail, setUpdatedEmail] = useState<string>(props.physician.emailAddress);
    const [isEditingEmail, setIsEditingEmail] = useState<boolean>(false);
    const [isEditingPassword, setIsEditingPassword] = useState<boolean>(false);
    const [changePasswordModel, setChangePasswordModel] = useState<ChangePasswordModel>({ email: props.physician.emailAddress } as ChangePasswordModel);
    const [showEmailSuccess, setShowEmailSuccess] = useState<boolean>(false);
    const [showPasswordSuccess, setShowPasswordSuccess] = useState<boolean>(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>(null);
    const [emailErrorMessage, setEmailErrorMessage] = useState<string>(null);
    const [validatePassword, setValidatePassword] = useState<boolean>(false);

    const authContext = useContext(AuthContext);
    const isNewPasswordValid = changePasswordModel.newPassword === changePasswordModel.confirmNewPassword;

    useEffect(() => {
        setEmailErrorMessage("");
    }, [isEditingEmail]);

    useEffect(() => {
        setPasswordErrorMessage("");
    }, [isEditingPassword]);

    useEffect(() => {
        var updated = deepCopy(changePasswordModel);
        updated.email = currentEmail;
        setChangePasswordModel(updated);
    }, [currentEmail]);

    const updateEmailAddress = (value: string) => {
        setUpdatedEmail(value);
    }

    const updateCurrentPassword = (value: string) => {
        var updated = deepCopy(changePasswordModel);
        updated.currentPassword = value;
        setChangePasswordModel(updated);
    }

    const updateNewPassword = (value: string) => {
        var updated = deepCopy(changePasswordModel);
        updated.newPassword = value;
        setChangePasswordModel(updated);
    }

    const updateConfirmPassword = (value: string) => {
        var updated = deepCopy(changePasswordModel);
        updated.confirmNewPassword = value;
        setChangePasswordModel(updated);
    }

    const cancelEditEmailAddress = () => {
        setUpdatedEmail(currentEmail);
        setIsEditingEmail(false);
    }

    const changeEmailAddress = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setEmailErrorMessage("");

        e.currentTarget.classList.add('was-validated');
        if (e.currentTarget.checkValidity()) {
            e.currentTarget.classList.remove('was-validated');
            setIsLoading(true);

            const isValid = await abpnClient.post<boolean>('/api/profile/ValidateEmail', { email: updatedEmail } as ActivateUser, authContext);

            if (isValid) {
                await abpnClient.post<void>('/api/Physician/UpdateEmail', { physicianId: props.physician.physicianId, currentEmail: currentEmail, newEmail: updatedEmail } as ChangeEmailModel, authContext);

                let changeEmailModel = { 
                    currentEmail: currentEmail, 
                    newEmail: updatedEmail, 
                    registerClientId: IdentityServerKnownClient.physicianPortal,
                    lastName: props.physician.lastName,
                    ssn4: props.physician.ssn4,
                    dateOfBirth: props.physician.dateOfBirth,
                    email: updatedEmail
                 } as ChangeEmailModel;
                const res = await authContext.changeEmail(changeEmailModel);
                if (res.ok) {
                    setCurrentEmail(updatedEmail);
                    setShowEmailSuccess(true);
                    setIsEditingEmail(false);
                    setTimeout(function () {
                        setShowEmailSuccess(false);
                    }, 5000);
                }
                else {
                    const text = await res.text();
                    setEmailErrorMessage(text);
                }
                setIsLoading(false);
            }
            else {
                setEmailErrorMessage("The email you provided is already in use. Please try a new email.");
                setIsLoading(false);
            }
        }
    }

    const changePassword = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setPasswordErrorMessage("");

        e.currentTarget.classList.add('was-validated');
        setValidatePassword(true)

        if (isNewPasswordValid && e.currentTarget.checkValidity()) {
            e.currentTarget.classList.remove('was-validated');
            setValidatePassword(false);

            setIsLoading(true);
            authContext.changePassword(changePasswordModel).then(async res => {
                if (res.ok) {
                    setShowPasswordSuccess(true);
                    setIsEditingPassword(false);
                    setTimeout(function () {
                        setShowPasswordSuccess(false);
                    }, 5000);
                }
                else {
                    const text = await res.text();
                    setPasswordErrorMessage(text);
                }
                setIsLoading(false);
            })
        }
    }

    return (
        <Card border="pp-secondary-grey">
            <Prompt
                when={isEditingEmail || isEditingPassword}
                message='You have unsaved changes, are you sure you want to leave?'
            />
            <Card.Body>
                <Card.Title className="font-weight-bolder" onClick={() => setOpenCollapse(!openCollapse)}>
                    <span className="text-pp-primary"><i className="font-weight-bolder las la-angle-down"></i> Security</span>
                </Card.Title>
                {isLoading ? <AbpnSpinner /> :
                    <Collapse in={openCollapse}>
                        <Row>
                            <Col xs={12} md={6}>
                                <Card className="mb-3">
                                    {showEmailSuccess && <Alert variant="pp-alert-success">Success! Your Email has been updated.</Alert>}
                                    <Card.Body>
                                        <Card.Title className="font-weight-bold">Personal Email Address
                                            <OverlayTrigger
                                                placement={"right"}
                                                overlay={<Tooltip id="personalEmail" bsPrefix="pp-tooltip">
                                                    Your email address will be used to login to your portal.  ABPN highly recommends keeping a personal email address on file to ensure you receive all communications regarding applications, exams, certification requirements, pertinent updates, and to access your Physician Portal.
                                                </Tooltip>}>
                                                <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                            </OverlayTrigger>
                                        </Card.Title>
                                        {isEditingEmail ?
                                            <Form onSubmit={(e) => changeEmailAddress(e)}>
                                                <Form.Group controlId="emailAddress">
                                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Personal Email Address</Form.Label>
                                                    <Form.Control type="email"
                                                        required
                                                        onChange={(e) => updateEmailAddress(e.target.value)}
                                                        defaultValue={updatedEmail ?? null} />
                                                    <span className="invalid-feedback pp-font-size-smallest">{emailErrorMessage}</span>
                                                </Form.Group>
                                                {emailErrorMessage &&
                                                    <Alert variant="pp-alert-danger">
                                                        <span className="font-weight-bolder">{emailErrorMessage}</span>
                                                    </Alert>}
                                                <div className="mt-4">
                                                    <Button className="mr-4 font-weight-bold" variant="pp-primary" type="submit">Save</Button>
                                                    <Button className="font-weight-bold" variant="outline-dark" onClick={cancelEditEmailAddress}>Cancel</Button>
                                                </div>
                                            </Form>
                                            :
                                            <Fragment>
                                                <Card.Text className="pp-font-size-smaller">{currentEmail}</Card.Text>
                                                <Button className="font-weight-bold" variant="pp-primary" onClick={() => setIsEditingEmail(true)}>Edit</Button>
                                            </Fragment>
                                        }
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12} md={6}>
                                <Card className="mb-3">
                                    {showPasswordSuccess && <Alert variant="pp-alert-success">Success! Your Password has been updated.</Alert>}
                                    <Card.Body>
                                        <Card.Title className="font-weight-bold">Password</Card.Title>
                                        {isEditingPassword ?
                                            <Fragment>
                                                <Form onSubmit={(e) => changePassword(e)}>
                                                    <Form.Group controlId="password" className="mt-4">
                                                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Old Password <span className="text-pp-primary">*</span></Form.Label>
                                                        <PasswordControl onChange={updateCurrentPassword} validate={false} validationMessage={null} isValid={true} />
                                                    </Form.Group>
                                                    <div className="pp-font-size-smaller mb-4">Use 8 or more characters to set your new password.</div>
                                                    <Form.Group>
                                                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">New Password <span className="text-pp-primary">*</span></Form.Label>
                                                        <PasswordControl onChange={updateNewPassword} validate={validatePassword} validationMessage="Does not match 'Confirm New Password'"
                                                            isValid={changePasswordModel.newPassword === changePasswordModel.confirmNewPassword} />
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Confirm New Password <span className="text-pp-primary">*</span></Form.Label>
                                                        <PasswordControl onChange={updateConfirmPassword} validate={validatePassword} validationMessage="Does not match 'New Password'"
                                                            isValid={changePasswordModel.newPassword === changePasswordModel.confirmNewPassword} />
                                                    </Form.Group>
                                                    {passwordErrorMessage &&
                                                        <Alert variant="pp-alert-danger">
                                                            <span className="font-weight-bolder">{passwordErrorMessage}</span>
                                                        </Alert>}
                                                    <div className="mt-4">
                                                        <Button onClick={() => setValidatePassword(true)} className="mr-4 font-weight-bold" variant="pp-primary" type="submit">Set Password</Button>
                                                        <Button className="font-weight-bold" variant="outline-dark" onClick={() => setIsEditingPassword(false)}>Cancel</Button>
                                                    </div>
                                                </Form>
                                            </Fragment>
                                            :
                                            <Button className="font-weight-bold" variant="pp-primary" onClick={() => setIsEditingPassword(true)}>Edit</Button>
                                        }
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Collapse>}
            </Card.Body>
        </Card>
    );
}